/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useRef } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Button } from '@aicon-dev/caas-ui';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';
import styled from 'styled-components';

import { MarkerIcon } from '~/assets/icons/MarkerIcon';
import { ImagePinchZoom } from '~/components/ui/ImagePinchZoom';
import { useNavigate, useParams } from '~/router';

import { findOneReportPhoto } from '../../libs/apis';
import { ReportListPhotoModel } from '../../libs/types';
import { getProcessText } from '../ReportListFilter';

export interface ReportPhotosDetailProps {
  data: ReportListPhotoModel;
}

export const ReportPhotosDetail = (props: ReportPhotosDetailProps) => {
  const { data } = props;
  const { t } = useTranslation('report');
  const { projectId } = useParams('/projects/:projectId/reports');
  const drawingImgRef = useRef(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    const res = await findOneReportPhoto(data.report_id || '', data._id, projectId);

    return res.data;
  };

  const { data: imageData } = useQuery({
    queryKey: [data.report_id, data._id, projectId],
    queryFn: fetchData,
  });

  const zoomInOutDrawingImg = useCallback(({ x, y, scale }: any) => {
    const { current: img }: any = drawingImgRef;

    if (drawingImgRef) {
      const value = make3dTransformValue({ x, y, scale });
      img.style.setProperty('transform', value);
    }
  }, []);

  const drawingImageCoords = useMemo(() => {
    return {
      x: imageData?.metadata?.x || 0,
      y: imageData?.metadata?.y || 0,
      width: imageData?.metadata?.width || 0,
      height: imageData?.metadata?.height || 0,
    };
  }, [imageData?.metadata?.width, imageData?.metadata?.height]);

  const imageRatioInfo = useMemo(() => {
    if (!data.width || !data.height) return {};
    return {
      className: 'ratio-mode',
      style: {
        paddingBottom: `${(data.height / data.width) * 100}%`,
      },
    };
  }, [data.width, data.height]);

  const goToReport = () => {
    if (!data.report_id) return;
    navigate('/projects/:projectId/reports/:reportId', {
      params: { projectId, reportId: data.report_id },
    });
  };

  return (
    <>
      <ImageWrap {...imageRatioInfo}>
        <ImagePinchZoom
          image={data}
          renderCustomPreview={(openViewer) => (
            <img className="thumb" src={data.url} onClick={openViewer} />
          )}
          imageStyle={{}}
        />
      </ImageWrap>
      <ContentWrap>
        {!!data.takenDate && (
          <>
            <Label>{t('report:photo_detail_created_at')}</Label>
            <ValueText mb="24px">{dayjs(data.takenDate).format('lll')}</ValueText>
          </>
        )}

        <Label>{t('report:photo_detail_process_name')}</Label>
        <ValueText mb="24px">{getProcessText(data.process?.title, data?.task?.text)}</ValueText>

        {!!data.metadata?.drawingFile_id && (
          <Box mb="12px">
            <Label>{t('report:photo_detail_drawing_name')}</Label>
            <ValueText mb="8px">{data.metadata?.drawingFile_name}</ValueText>
            <Box
              position="relative"
              paddingBottom={`${(drawingImageCoords.height / drawingImageCoords.width) * 100}%`}
            >
              <Box position="absolute" top={0} left={0} width="100%" height="100%">
                <QuickPinchZoom onUpdate={zoomInOutDrawingImg} key={data?.metadata._id}>
                  <div ref={drawingImgRef}>
                    <div
                      style={{
                        position: 'absolute',
                        left: `${(drawingImageCoords.x / drawingImageCoords.width) * 100}%`,
                        top: `${(drawingImageCoords.y / drawingImageCoords.height) * 100}%`,
                        transform: 'translate(-50%, -100%)',
                      }}
                    >
                      <MarkerIcon size="17" style={{ display: 'block' }} />
                    </div>

                    <div>
                      <img
                        src={imageData?.metadata?.pdfImageUrl}
                        style={{
                          width: '100%',
                          height: '100%',
                          background: 'var(--white)',
                        }}
                      />
                    </div>
                  </div>
                </QuickPinchZoom>
              </Box>
            </Box>
          </Box>
        )}

        {!!data.memo && (
          <>
            <Label>{t('report:photo_detail_memo')}</Label>
            <ValueText mb="24px">{data.memo}</ValueText>
          </>
        )}

        {!!data.location && (
          <>
            <Label>{t('report:photo_detail_location')}</Label>
            <ValueText mb="24px">{data.location}</ValueText>
          </>
        )}

        <Button
          block
          color="primary"
          size="large"
          variant="filled"
          style={{ marginTop: 56 }}
          onClick={goToReport}
        >
          {t('report:photo_detail_go_to_report')}
        </Button>
      </ContentWrap>
    </>
  );
};

const ImageWrap = styled.div`
  position: relative;
  margin-top: 8px;
  background-color: #d9d9d9;

  img.thumb {
    width: 100%;
    height: auto;
  }
  &.ratio-mode {
    img.thumb {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const ContentWrap = styled.div`
  padding: 12px 16px 48px;
`;

const Label = styled.div`
  color: var(--color-primary-400);
  font-size: 14px;
  margin-bottom: 4px;
`;

const ValueText = styled(Box)`
  font-weight: 400;
  font-size: 18px;
  line-height: 23px;
  color: #2c384a;
`;
